import axios, { AxiosInstance } from "axios";
import { inject, observer } from "mobx-react";
import React from "react";
import Lightbox from "react-18-image-lightbox";
import { ReactNotifications, Store } from "react-notifications-component";
import { useNavigate } from "react-router-dom";
import { fetchToken, onMessageListener } from "./helper/firebase";

import Content from "./components/content/content";
import Loading_bar from "./components/control/loading_bar";
import CommonModal from "./components/control/modal";
import { HEADER, RESULT_CODE } from "./config/const";
import { Page_settings } from "./config/page_settings.js";
import strings from "./lang/strings";
import Layout from "./components/content/layout";
import "./assets/fonts/fonts.css";
import api_interceptor from "./config/api_interceptor";
import { rootStore } from "./mobx/store";

const BUCKET_URL = process.env.REACT_APP_BUCKET_URL;

interface AppProps {
  rootStore: {
    token: string;
    getSetting: any;
  };
  navigate: (path: string) => void;
}

interface NotificationType {
  type: "success" | "danger" | "info" | "default" | "warning";
}

interface AppState {
  hasScroll: boolean;
  pageHeader: number;
  plannerPageHeader: boolean;
  pageSidebar: boolean;
  pageSidebarToggled: boolean;
  pageHeaderFixed: boolean;
  profileModal: boolean;
  settingModal: boolean;
  content: boolean;
  termDetailModal: boolean;
  billSendModal: boolean;
  notificationModal: boolean;
  changePasswordModal: boolean;
  signOutModal: boolean;
  termModal: boolean;
  accountModal: boolean;
  termType: number;
  headerType: number;
  billId: string;
  aiTestItem: any;
  pageLoading: boolean;
  modal: {
    show: boolean;
    title: string;
    text: string;
    subText: string;
    okBtn: string;
    cancelBtn: string;
    cb: ((res: any) => void) | null;
  };
  galleryShow: boolean;
  galleryData: any[];
  galleryIndex: number;
  member_detail: {
    isOpen: boolean;
    id: number;
    callback: ((res: any) => void) | null;
  };
  isTokenFound: boolean;
  handleSetPageHeader?: (value: number) => void;
  handleSetPlannerPageHeader?: (value: boolean) => void;
  handleSetPageHeaderFixed?: (value: boolean) => void;
  handleSetPageSidebar?: (value: boolean) => void;
  toggleMobileSidebar?: () => void;
  handleSetContent?: (value: boolean) => void;
  handleSetPageBoxedLayout?: (value: boolean) => void;
  handleSettingModal?: (value: boolean) => void;
  handleProfileModal?: (value: boolean) => void;
  handleBillSendModal?: (value: boolean) => void;
  handleNotificationModal?: (value: boolean) => void;
  handleTermModal?: (value: boolean) => void;
  handleAccountModal?: (value: boolean) => void;
  handleChangePasswordModal?: (value: boolean) => void;
  handleSignOutModal?: (value: boolean) => void;
  handleTermDetail?: (value: boolean) => void;
  setTermType?: (value: number) => void;
  setHeaderType?: (value: number) => void;
  setBillId?: (value: string) => void;
  setAITestItem?: (value: any) => void;
  toggleLoadingBar?: (isLoading: boolean) => void;
  showModal?: (
    title: string,
    text: string,
    okBtn: string,
    cancelBtn: string,
    cb?: (res: any) => void
  ) => void;
  showAlert?: (text: string, cb?: (res: any) => void, okBtn?: string) => void;
  showConfirm?: (
    text: string,
    cb?: (res: any) => void,
    okBtn?: string,
    cancelBtn?: string
  ) => void;
  showUserPopup?: (user_id: number, callback?: (res: any) => void) => void;
  addNotification?: (
    title: string,
    message: string,
    type?: NotificationType["type"]
  ) => void;
  showGallery?: (images: any[], index: number) => void;
  post?: (
    uri: string,
    params: any,
    cb?: (data: any) => void,
    errCb?: (error: string) => void,
    showProgress?: boolean
  ) => Promise<void>;
  get?: (
    uri: string,
    params: any,
    cb?: (data: any) => void,
    errCb?: (error: string) => void,
    showProgress?: boolean
  ) => Promise<void>;
  delete?: (
    uri: string,
    params: any,
    cb?: (data: any) => void,
    errCb?: (error: string) => void,
    showProgress?: boolean
  ) => Promise<void>;
  download?: (
    method: string,
    uri: string,
    params: any,
    filename?: string,
    showProgress?: boolean
  ) => Promise<void>;
  put?: (
    uri: string,
    params: any,
    cb?: (data: any) => void,
    errCb?: (error: string) => void,
    showProgress?: boolean
  ) => Promise<void>;
  getSettingName?: (code: string, ...name: string[]) => string;
  getSettingList?: (...name: string[]) => any;
  loadImage?: (...url: string[]) => string;
}

interface CommonModalProps {
  show: boolean;
  title: string;
  text: string;
  subText: string;
  okBtn: string;
  cancelBtn: string;
  cb: ((res: any) => void) | null;
  onModalClose: (res: any) => void;
}

const withNavigation = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  };
};

const CommonModalComponent: React.FC<CommonModalProps> = (props) => {
  return <CommonModal {...props} />;
};

class App extends React.Component<AppProps, AppState> {
  private api: AxiosInstance;
  private toggleLoadingBar: (isLoading: boolean) => void;
  private addNotification: (
    title: string,
    message: string,
    type?: NotificationType["type"]
  ) => void;
  private showModal: (
    title: string,
    text: string,
    okBtn: string,
    cancelBtn: string,
    cb?: (res: any) => void
  ) => void;
  private showAlert: (
    text: string,
    cb?: (res: any) => void,
    okBtn?: string
  ) => void;
  private showConfirm: (
    text: string,
    cb?: (res: any) => void,
    okBtn?: string,
    cancelBtn?: string
  ) => void;
  private showUserPopup: (
    user_id: number,
    callback?: (res: any) => void
  ) => void;
  private hideUserPopup: () => void;
  private getSettingList: (...name: string[]) => any;
  private getSettingName: (code: string, ...name: string[]) => string;
  private loadImage: (...url: string[]) => string;
  private post: (
    uri: string,
    params: any,
    cb?: (data: any) => void,
    errCb?: (error: string) => void,
    showProgress?: boolean
  ) => Promise<void>;
  private get: (
    uri: string,
    params: any,
    cb?: (data: any) => void,
    errCb?: (error: string) => void,
    showProgress?: boolean
  ) => Promise<void>;
  private put: (
    uri: string,
    params: any,
    cb?: (data: any) => void,
    errCb?: (error: string) => void,
    showProgress?: boolean
  ) => Promise<void>;
  private delete: (
    uri: string,
    params: any,
    cb?: (data: any) => void,
    errCb?: (error: string) => void,
    showProgress?: boolean
  ) => Promise<void>;
  private request: (
    method: string,
    uri: string,
    params: any,
    cb?: (data: any) => void,
    errCb?: (error: string) => void,
    showProgress?: boolean
  ) => Promise<void>;
  private download: (
    method: string,
    uri: string,
    params: any,
    filename?: string,
    showProgress?: boolean
  ) => Promise<void>;
  private handleProfileModal: (value: boolean) => void;
  private handleBillSendModal: (value: boolean) => void;
  private handleSettingModal: (value: boolean) => void;
  private handleApiError: (e: any) => void;
  private handleNotificationModal: (value: boolean) => void;
  private handleTermModal: (value: boolean) => void;
  private handleAccountModal: (value: boolean) => void;
  private handleChangePasswordModal: (value: boolean) => void;
  private handleSignOutModal: (value: boolean) => void;
  private handleTermDetail: (value: boolean) => void;
  private setTermType: (value: number) => void;
  private setHeaderType: (value: number) => void;
  private setBillId: (value: string) => void;
  private setAITestItem: (value: any) => void;
  private handleSetPageHeader: (value: number) => void;
  private handleSetPlannerPageHeader: (value: boolean) => void;
  private handleSetPageHeaderFixed: (value: boolean) => void;
  private handleSetPageSidebar: (value: boolean) => void;
  private toggleMobileSidebar: () => void;
  private handleSetContent: (value: boolean) => void;
  private handleSetPageBoxedLayout: (value: boolean) => void;
  private showGallery: (images: any[], index: number) => void;
  private setNotification: (notification: {
    title: string;
    body: string;
  }) => void;

  constructor(props: AppProps) {
    super(props);

    this.toggleLoadingBar = (isLoading) => {
      this.setState({ pageLoading: isLoading });
    };

    this.addNotification = (
      title: string,
      message: string,
      type: NotificationType["type"] = "success"
    ) => {
      Store.addNotification({
        title,
        message,
        type,
        insert: "top",
        container: "top-right",
        dismiss: {
          duration: 5000,
        },
      });
    };

    this.showModal = (title, text, okBtn, cancelBtn, cb) => {
      this.setState({
        modal: {
          show: true,
          title: title,
          text: text,
          subText: "",
          okBtn: okBtn,
          cancelBtn: cancelBtn,
          cb: cb,
        },
      });
    };
    this.showAlert = (text, cb, okBtn = strings.modal.confirm) => {
      this.showModal(strings.modal.alert, text, okBtn, "", cb);
    };
    this.showConfirm = (
      text,
      cb,
      okBtn = strings.modal.ok,
      cancelBtn = strings.modal.no
    ) => {
      this.showModal(strings.modal.confirm, text, okBtn, cancelBtn, cb);
    };
    this.showUserPopup = (user_id, callback) => {
      this.setState((state) => ({
        member_detail: {
          ...state.member_detail,
          isOpen: true,
          id: user_id,
          callback: callback,
        },
      }));
    };
    this.hideUserPopup = () => {
      this.setState({
        member_detail: {
          isOpen: false,
          id: 0,
          callback: null,
        },
      });
    };
    this.getSettingList = (...name: string[]) => {
      let item = this.props.rootStore.getSetting;
      for (const prop of name) {
        item = item[prop];
      }
      return item;
    };
    this.getSettingName = (code: string, ...name: string[]) => {
      let item = this.props.rootStore.getSetting;

      if (name[0] === "auth") {
        return item[name[0]]?.find((it: any) => it.code === code)?.name ?? "";
      }

      for (const prop of name) {
        item = item[prop];
      }
      return Array.isArray(item)
        ? item?.find((it: any) => it.code === code)?.name ?? ""
        : item[code];
    };


    // 리프레시 토큰 사용을 위해 interceptor 적용 axios
    this.api = api_interceptor;

    this.loadImage = (...url) => {
      return BUCKET_URL + "/" + url;
    };
    this.post = async (uri, params, cb, errCb, showProgress = true) => {
      await this.request("POST", uri, params, cb, errCb, showProgress);
    };
    this.get = async (uri, params, cb, errCb, showProgress = true) => {
      await this.request("GET", uri, params, cb, errCb, showProgress);
    };
    this.put = async (uri, params, cb, errCb, showProgress = true) => {
      await this.request("PUT", uri, params, cb, errCb, showProgress);
    };
    this.delete = async (uri, params, cb, errCb, showProgress = true) => {
      await this.request(
        "DELETE",
        uri,
        { data: params },
        cb,
        errCb,
        showProgress
      );
    };
    this.request = async (
      method,
      uri,
      params,
      cb,
      errCb,
      showProgress = true
    ) => {
      if (showProgress) {
        this.toggleLoadingBar(true);
      }

      this.api.defaults.responseType = "json";

      try {
        let { data } =
          method !== "GET"
            ? await this.api[method.toLowerCase()](uri, params)
            : await this.api.get(uri, { params });

        if (showProgress) {
          this.toggleLoadingBar(false);
        }

        if (Number(data.result_code) === RESULT_CODE.SUCCESS) {
          cb?.(data.result_data);
        } else {
          if (
            Number(data.result_code) === RESULT_CODE.ERROR &&
            uri === "auth/login" &&
            params.loginType !== "EMAIL"
          ) {
            cb?.(data);
          } else if (uri === "auth/login" && params.loginType === "EMAIL") {
            this.showAlert(data.result_msg);
            this.props.navigate("/login/email");
          } else {
            errCb ? errCb(data.result_msg) : this.handleApiError(data);
          }
        }
      } catch (err) {
        if (showProgress) {
          this.toggleLoadingBar(false);
        }
        errCb ? errCb(err.message) : this.showAlert(err.message);
      }
    };
    this.download = async (
      method,
      uri,
      params,
      filename,
      showProgress = true
    ) => {
      if (showProgress) {
        this.toggleLoadingBar(true);
      }

      this.api.defaults.headers.common[HEADER.AUTH_TOKEN] = rootStore.token;
      this.api.defaults.responseType = "blob";

      try {
        let response =
          method !== "GET"
            ? await this.api[method.toLowerCase()](uri, params)
            : await this.api.get(uri, { params });

        if (showProgress) {
          this.toggleLoadingBar(false);
        }
        const filenameCur = filename
          ? filename
          : response.headers.get("content-disposition")
          ? response.headers
              .get("content-disposition")
              .split("filename=")[1]
              .replace(/"/g, "")
          : "downloaded_file";
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filenameCur); //or any other extension
        document.body.appendChild(link);
        link.click();
      } catch (err) {
        console.log(err);

        if (showProgress) {
          this.toggleLoadingBar(false);
        }

        this.showAlert(err.message);
      }
    };
    this.handleProfileModal = (value) => {
      this.setState({
        profileModal: value,
      });
    };

    this.handleBillSendModal = (value) => {
      this.setState({
        billSendModal: value,
      });
    };

    this.handleSettingModal = (value) => {
      this.setState({
        settingModal: value,
      });
    };
    // this.handleApiError = (e) => {
    //   if (
    //     Number(e.result_code) === RESULT_CODE.EMPTY_TOKEN ||
    //     Number(e.result_code) === RESULT_CODE.TOKEN_EXPIRED
    //   ) {
    //     this.props.navigate("/login");

    //     // localStorage 개별 삭제 (saved_email과 recent_login_platform 보존)
    //     localStorage.removeItem("admin");
    //     localStorage.removeItem("user_info");
    //     localStorage.removeItem("refresh_token");
    //     localStorage.removeItem("signup_info");
    //     localStorage.removeItem("corp_info");

    //     return;
    //   }

    //   this.showAlert(e.result_msg);
    // };

    this.handleApiError = (e) => {
      const currentPath = window.location.pathname;

      if (
        Number(e.result_code) === RESULT_CODE.EMPTY_TOKEN ||
        Number(e.result_code) === RESULT_CODE.TOKEN_EXPIRED
      ) {
        // 이미 /login 경로면 navigate 반복 방지
        if (!currentPath.includes("/login")) {
          this.props.navigate("/login");
        }

        // localStorage에서 불필요한 데이터 정리
        localStorage.removeItem("admin");
        localStorage.removeItem("user_info");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("signup_info");
        localStorage.removeItem("corp_info");

        // rootStore 초기화도 함께 처리
        rootStore.signOut();

        return;
      }

      this.showAlert(e.result_msg);
    };

    this.handleNotificationModal = (value) => {
      this.setState({
        notificationModal: value,
      });
    };

    this.handleTermModal = (value) => {
      this.setState({
        termModal: value,
      });
    };

    this.handleAccountModal = (value) => {
      this.setState({
        accountModal: value,
      });
    };

    this.handleChangePasswordModal = (value) => {
      this.setState({
        changePasswordModal: value,
      });
    };

    this.handleSignOutModal = (value) => {
      this.setState({
        signOutModal: value,
      });
    };

    this.handleTermDetail = (value) => {
      this.setState({
        termDetailModal: value,
      });
    };

    this.setTermType = (value) => {
      this.setState({
        termType: value,
      });
    };

    this.setHeaderType = (value) => {
      this.setState({
        headerType: value,
      });
    };

    this.setBillId = (value) => {
      this.setState({
        billId: value,
      });
    };

    this.setAITestItem = (value) => {
      this.setState({
        aiTestItem: value,
      });
    };

    this.state = {
      hasScroll: false,
      pageHeader: 1,
      plannerPageHeader: false,
      pageSidebar: true,
      pageSidebarToggled: false,
      pageHeaderFixed: true,
      profileModal: false,
      settingModal: false,
      content: true,
      termDetailModal: false,
      billSendModal: false,
      notificationModal: false,
      changePasswordModal: false,
      signOutModal: false,
      termModal: false,
      accountModal: false,
      termType: 1,
      headerType: 1,
      billId: "",
      aiTestItem: null,
      pageLoading: false,
      modal: {
        show: false,
        title: "",
        text: "",
        subText: "",
        okBtn: "",
        cancelBtn: "",
        cb: null,
      },
      galleryShow: false,
      galleryData: [],
      galleryIndex: 0,
      member_detail: {
        isOpen: false,
        id: 0,
        callback: null,
      },
      isTokenFound: false,
      toggleLoadingBar: this.toggleLoadingBar,
      showModal: this.showModal,
      showAlert: this.showAlert,
      showConfirm: this.showConfirm,
      showUserPopup: this.showUserPopup,
      addNotification: this.addNotification,
      showGallery: this.showGallery,
      post: this.post,
      get: this.get,
      delete: this.delete,
      download: this.download,
      put: this.put,
      getSettingName: this.getSettingName,
      getSettingList: this.getSettingList,
      loadImage: this.loadImage,
    };
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll);

    // 토큰 초기화
    rootStore.initializeToken();

    // firebase
    fetchToken((tokenFound) => this.setState({ isTokenFound: tokenFound }));
    onMessageListener()
      .then((payload) => {
        this.setNotification({
          title: payload.notification.title,
          body: payload.notification.body,
        });
      })
      .catch((err) => console.log("failed: ", err));

    // 전역 에러 핸들러 설정
    window.onerror = (
      message: string | Event,
      source: string,
      lineno: number,
      colno: number,
      error: Error | null
    ) => {
      if (
        typeof message === "string" &&
        message.includes("you can only call it when room is writable")
      ) {
        console.log(
          "Caught and ignored 'you can only call it when room is writable' error"
        );
        return true;
      }

      if (
        error instanceof URIError ||
        (typeof message === "string" && message.includes("URIError"))
      ) {
        console.log("Caught and ignored URIError:", message);
        return true;
      }

      return false;
    };

    // 미처리된 Promise 거부 처리
    window.addEventListener("unhandledrejection", (event) => {
      if (
        event.reason.message.includes(
          "you can only call it when room is writable"
        )
      ) {
        // 에러를 무시하고 아무 작업도 하지 않음
        console.log(
          "Caught and ignored 'you can only call it when room is writable' error"
        );
        event.preventDefault(); // 에러를 무시하고 전파하지 않음
      }

      // URIError 처리 추가
      if (
        event.reason instanceof URIError ||
        (event.reason.message && event.reason.message.includes("URIError"))
      ) {
        console.log("Caught and ignored URIError in promise:", event.reason);
        event.preventDefault(); // 에러를 무시하고 전파하지 않음
      }
    });
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll = () => {
    this.setState({
      hasScroll: window.scrollY > 0,
    });
    let elm = document.getElementsByClassName("nvtooltip");
    for (let i = 0; i < elm.length; i++) {
      const element = elm[i] as HTMLElement;
      element.classList.add("d-none");
    }
  };

  onModalClose = (res) => {
    this.setState(
      (state) => ({
        modal: { ...state.modal, show: false },
      }),
      () => this.state.modal.cb?.(res)
    );
  };

  render() {
    return (
      <Page_settings.Provider value={this.state}>
        <Layout>
          <ReactNotifications />
          <Content rootStore={this.props.rootStore} />
          <CommonModalComponent
            {...this.state.modal}
            onModalClose={this.onModalClose}
          />
          {this.state.pageLoading && <Loading_bar />}
        </Layout>
      </Page_settings.Provider>
    );
  }
}

export default withNavigation(inject("rootStore")(observer(App)));
